package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.outlined.MarkEmailUnread
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.ListItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.ChatThread
import com.ijoin.data.model.ChatUserSummary
import com.ijoin.i18n.Strings
import com.ijoin.i18n.strings
import com.ijoin.ui.chat.ChatListViewModel
import com.ijoin.ui.components.NavigationScaffold
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventPill
import com.ijoin.ui.design.EventSearchField
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.test.TestTags
import com.ijoin.util.datetime.monthNumberCompat
import kotlinx.datetime.LocalDateTime
import kotlinx.coroutines.delay

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChatListScreen(
    onBackClick: () -> Unit,
    onChatSelected: (ChatThread) -> Unit,
    onStartChatWithUser: (userId: String, displayName: String?) -> Unit,
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: ChatListViewModel = remember { ChatListViewModel() },
) {
    val strings = strings()
    val uiState by viewModel.uiState.collectAsState()
    var searchQuery by remember { mutableStateOf("") }
    val onTabSelected: (String) -> Unit = { tab ->
        when (tab) {
            "home" -> onOpenHome()
            "explore" -> onOpenExplore()
            "create" -> onCreateEventClick()
            "tickets" -> onOpenTickets()
            "profile" -> onOpenProfile()
        }
    }

    LaunchedEffect(searchQuery) {
        if (searchQuery.isBlank()) {
            viewModel.clearSearch()
        } else {
            delay(300)
            viewModel.searchUsers(searchQuery)
        }
    }

    NavigationScaffold(
        selectedTab = "home",
        onTabSelected = onTabSelected,
        modifier = modifier.testTag(TestTags.ChatListScreen.ROOT),
        containerColor = EventCardBackground,
        topBar = {
            TopAppBar(
                title = { Text(strings.messagesTitle) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = strings.back,
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = EventCardBackground,
                    titleContentColor = EventCardAccentDark,
                    navigationIconContentColor = EventCardAccentDark
                )
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(eventBackgroundBrush())
                .padding(paddingValues)
        ) {
            EventSearchField(
                value = searchQuery,
                onValueChange = { searchQuery = it },
                placeholder = strings.chatSearchPlaceholder,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 12.dp)
                    .testTag(TestTags.ChatListScreen.SEARCH_FIELD)
            )

            Box(
                modifier = Modifier
                    .fillMaxSize()
            ) {
                if (searchQuery.isNotBlank()) {
                    when {
                        uiState.isSearching -> {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                CircularProgressIndicator()
                            }
                        }
                        uiState.searchError != null -> {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = uiState.searchError?.ifBlank { null } ?: strings.chatSearchError,
                                    color = EventCardAlert,
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }
                        }
                        uiState.searchResults.isEmpty() -> {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = strings.chatSearchNoResults,
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                )
                            }
                        }
                        else -> {
                            EventSurfaceCard(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 16.dp, vertical = 8.dp),
                                contentPadding = PaddingValues(0.dp),
                                shadowElevation = 1.dp
                            ) {
                                LazyColumn(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentPadding = PaddingValues(vertical = 8.dp)
                                ) {
                                    items(uiState.searchResults) { user ->
                                        ChatSearchResultItem(
                                            user = user,
                                            onClick = {
                                                onStartChatWithUser(
                                                    user.id,
                                                    user.displayName?.takeIf { it.isNotBlank() }
                                                        ?: user.username
                                                )
                                                searchQuery = ""
                                                viewModel.clearSearch()
                                            },
                                        )
                                    }
                                }
                            }
                        }
                    }
                } else {
                    when {
                        uiState.isLoading && uiState.chats.isEmpty() -> {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                CircularProgressIndicator()
                            }
                        }
                        uiState.error != null && uiState.chats.isEmpty() -> {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = uiState.error?.ifBlank { null } ?: strings.chatLoadError,
                                    color = EventCardAlert,
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }
                        }
                        uiState.chats.isEmpty() -> {
                            Box(
                                modifier = Modifier.fillMaxSize(),
                                contentAlignment = Alignment.Center
                            ) {
                                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                    Icon(
                                        imageVector = Icons.Outlined.MarkEmailUnread,
                                        contentDescription = null,
                                        tint = EventCardAccentDark,
                                        modifier = Modifier.size(48.dp)
                                    )
                                    Spacer(modifier = Modifier.height(16.dp))
                                    Text(
                                        text = strings.emptyChatList,
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    )
                                }
                            }
                        }
                        else -> {
                            EventSurfaceCard(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 16.dp, vertical = 8.dp),
                                contentPadding = PaddingValues(0.dp),
                                shadowElevation = 1.dp
                            ) {
                                LazyColumn(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .testTag(TestTags.ChatListScreen.LIST),
                                    contentPadding = PaddingValues(vertical = 8.dp)
                                ) {
                                    items(uiState.chats) { chat ->
                                        ChatListItem(
                                            chat = chat,
                                            onClick = { onChatSelected(chat) },
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ChatListItem(
    chat: ChatThread,
    onClick: () -> Unit,
) {
    val strings = strings()
    val displayTitle = when {
        chat.group -> chat.title?.takeIf { it.isNotBlank() } ?: strings.groupChatLabel
        else -> chat.otherUserName?.takeIf { it.isNotBlank() } ?: strings.contactOrganizer
    }
    val supporting = when {
        !chat.lastMessagePreview.isNullOrBlank() -> chat.lastMessagePreview
        chat.group -> strings.groupChatSubtitle
        else -> strings.emptyMessages
    }
    val hasUnread = chat.unreadCount > 0
    val timestamp = strings.formatTimestamp(chat.lastMessageAt)
    EventSurfaceCard(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .testTag("${TestTags.ChatListScreen.THREAD_ITEM_PREFIX}${chat.id}"),
        cornerRadius = EventCardCompactCorner,
        backgroundColor = if (hasUnread) EventCardAccentSoft else EventCardSurface,
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
        shadowElevation = if (hasUnread) 2.dp else 1.dp
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically
            ) {
                val name = displayTitle
                    .ifBlank { chat.otherUserId?.takeIf { it.isNotBlank() } ?: "?" }
                val initial = name.firstOrNull()?.uppercase() ?: "?"
                Box(
                    modifier = Modifier
                        .size(44.dp)
                        .clip(CircleShape)
                        .background(if (hasUnread) EventCardSurface else EventCardAccentSoft),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = initial,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = EventCardAccentDark,
                    )
                }
                Spacer(modifier = Modifier.width(12.dp))
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(2.dp)
                ) {
                    Text(
                        text = displayTitle,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        color = EventCardAccentDark
                    )
                    Text(
                        text = supporting,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                if (timestamp.isNotEmpty()) {
                    Text(
                        text = timestamp,
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                if (hasUnread) {
                    EventPill(
                        text = chat.unreadCount.toString(),
                        backgroundColor = EventCardAccent,
                        textColor = EventCardSurface,
                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun ChatSearchResultItem(
    user: ChatUserSummary,
    onClick: () -> Unit,
) {
    val strings = strings()
    val title = user.displayName?.takeIf { it.isNotBlank() }
        ?: user.username?.takeIf { it.isNotBlank() }
        ?: user.id
    val details = buildList {
        if (!user.username.isNullOrBlank()) {
            add("@${user.username}")
        }
        if (user.organizer) {
            add(strings.organizerLabel)
        }
    }.joinToString(separator = " • ")

    EventSurfaceCard(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .testTag("${TestTags.ChatListScreen.SEARCH_RESULT_PREFIX}${user.id}"),
        cornerRadius = EventCardCompactCorner,
        contentPadding = PaddingValues(0.dp),
        shadowElevation = 1.dp
    ) {
        ListItem(
            headlineContent = {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            },
            supportingContent = {
                if (details.isNotBlank()) {
                    Text(
                        text = details,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            },
            leadingContent = {
                val name = user.displayName?.takeIf { it.isNotBlank() }
                    ?: user.username?.takeIf { it.isNotBlank() }
                    ?: user.id
                val initial = name.firstOrNull()?.uppercase() ?: "?"
                Box(
                    modifier = Modifier
                        .size(44.dp)
                        .clip(MaterialTheme.shapes.small)
                        .background(EventCardAccentSoft),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = initial,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = EventCardAccentDark,
                    )
                }
            }
        )
    }
}

private fun Strings.formatTimestamp(timestamp: String?): String {
    if (timestamp.isNullOrBlank()) return ""
    return try {
        val dateTime = LocalDateTime.parse(timestamp)
        val date = dateTime.date
        val time = dateTime.time
        val day = date.dayOfMonth
        val month = shortMonthName(date.monthNumberCompat())
        val hour = time.hour.toString().padStart(2, '0')
        val minute = time.minute.toString().padStart(2, '0')
        "$day $month $hour:$minute"
    } catch (e: Exception) {
        timestamp.replace("T", " ").take(16)
    }
}
